import { ReactNode, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuthStore } from '../stores/auth-store';
import { Loader2 } from 'lucide-react';

// Check if we're in dev mode without Azure AD configured
const isDevModeWithoutAuth = import.meta.env.DEV && !import.meta.env.VITE_AZURE_AD_CLIENT_ID;

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { login, checkAuth } = useAuthStore();

  // Use MSAL React hooks - these properly wait for redirect processing
  const { inProgress, accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();

  // Prevent multiple login redirects
  const loginTriggered = useRef(false);

  // Sync MSAL state to auth store when authentication completes
  useEffect(() => {
    // Only sync after MSAL has finished any interaction (including redirect processing)
    if (inProgress === InteractionStatus.None) {
      checkAuth();
    }
  }, [inProgress, accounts, checkAuth]);

  // Trigger login when not authenticated (only once)
  useEffect(() => {
    if (
      !isDevModeWithoutAuth &&
      inProgress === InteractionStatus.None &&
      !isMsalAuthenticated &&
      !loginTriggered.current
    ) {
      loginTriggered.current = true;
      login();
    }
  }, [inProgress, isMsalAuthenticated, login]);

  // Reset login trigger when user becomes authenticated
  useEffect(() => {
    if (isMsalAuthenticated) {
      loginTriggered.current = false;
    }
  }, [isMsalAuthenticated]);

  // Dev mode bypass
  if (isDevModeWithoutAuth) {
    return <>{children}</>;
  }

  // Show loading while MSAL is processing (including redirect from login)
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary-600" />
          <p className="mt-4 text-gray-600">
            {inProgress === InteractionStatus.HandleRedirect
              ? 'Completing sign-in...'
              : 'Authenticating...'}
          </p>
        </div>
      </div>
    );
  }

  // After MSAL is done, check if authenticated
  if (!isMsalAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">BetterMatch</h1>
          <p className="mt-2 text-gray-600">Redirecting to login...</p>
          <Loader2 className="mx-auto mt-4 h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
